import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";
import { Input, Modal, Badge, Avatar, Form, Select, DatePicker, Space, Card, Row, Col, Spin, message } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { useSelector } from "react-redux";
import styled from "styled-components";
import dayjs from "dayjs";
import { ButtonFilled } from "../../../Reusablecomponents/UIButtons";
import { StyledTable } from "../../../Reusablecomponents/UITable/UITable";
import filtericon from "../../../assets/images/filter-svgrepo-com (1).svg";
import upload from "../../../assets/images/upload.svg";
import legco2 from "../../../assets/images/kindpng_925383 1.svg";
import { shipmentType } from "../../../Common/constant";
import { handleDownloadChart } from "../../../Reusablecomponents/Export/Exportpdf";
import { CALL_API } from "../../../Common/api";
import ReusablePagination from "../../../Reusablecomponents/ReusablePagination";
import { fetchNexusAssets, fetchNexusFences } from "../../TrackRightAI/nexusTrakingApi";
import { withNexusReportShell } from "../NexusReportShell";

// Nexus Carbon Footprint Report: same page and charts as the AI Carbon
// Footprint Report, built from the carbon footprint saved on each Nexus trip
// (POST nexus/carbonreport).

const { Option } = Select;
const { RangePicker } = DatePicker;

const Customstyle = styled.div`
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .Custom {
    display: flex;
    align-items: center;
    gap: 20px;
  }
  .dropstyle {
    margin-top: 20px !important;
    width: 250px !important;
  }
  .dropstyle1 {
    height: 36px;
    width: 250px !important;
  }
  .head1 {
    font-size: 16px;
    font-weight: 400;
  }
  .textstyle {
    font-size: 16px;
    font-weight: 500;
  }
  .head2 {
    color: #5f6d8c;
  }
  .badg-hover-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: -2px;
  }
  .hoverimage1 {
    margin-top: -15px;
  }
  .tooltipContainer {
    position: relative;
    display: inline-block;
    text-align: center;
  }
  .tooltipContent {
    background: #ffffff;
    color: #333;
    padding: 10px 12px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    font-size: 13px;
    min-width: 100px;
  }
  .label {
    font-weight: bold;
    margin-bottom: 5px;
  }
  .item {
    display: flex;
    justify-content: space-between;
    gap: 4px;
  }
  @media (max-width: 1024px) {
    .legend-image {
      display: none;
    }
  }
`;

const COLORS = ["#FFCE4F", "#4FFFE2", "#F64FFF", "#FF4F52", "#63FF4F", "#FFFC4F", "#5A56E9"];

const Duration = [
  { label: "Current Month", value: "current_month" },
  { label: "Current Year", value: "current_year" },
  { label: "Last Month", value: "last_month" },
  { label: "Today", value: "today" },
];

const rangeFor = (duration) => {
  if (duration === "today") return [dayjs(), dayjs()];
  if (duration === "last_month")
    return [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")];
  if (duration === "current_year") return [dayjs().startOf("year"), dayjs().endOf("year")];
  return [dayjs().startOf("month"), dayjs().endOf("month")];
};

const formatMinutes = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "-";
  const total = Math.round(Number(value));
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="tooltipContainer">
        <div className="tooltipContent">
          <div className="label">{label}</div>
          {payload.map((entry, index) => (
            <div key={index} className="item">
              <strong> {entry.value}</strong>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const VerticalChart = ({ data, width, height, barColor }) => (
  <ResponsiveContainer width={width} height={height}>
    <BarChart layout="vertical" margin={{ top: 10, right: 50, left: -20, bottom: 10 }} barSize={25} data={data}>
      <CartesianGrid vertical={false} horizontal={false} />
      <XAxis
        type="number"
        axisLine={false}
        tickLine={false}
        interval={0}
        tick={{ fontSize: 12, textAnchor: "start" }}
        tickFormatter={(value) => Math.round(value)}
      />
      <YAxis
        type="category"
        dataKey="Vehicle_No"
        axisLine={false}
        tickLine={false}
        width={150}
        tick={{ fontSize: 12, textAnchor: "end" }}
        label={{
          value: "(KgCO2)",
          angle: 0,
          position: "bottom",
          offset: 8,
          dx: 35,
          style: { fontSize: 14, fill: "#000" },
        }}
      />
      <Tooltip content={<CustomTooltip />} />
      <Bar dataKey="value" fill={barColor} radius={5} style={{ cursor: "pointer" }}>
        <LabelList
          content={({ x, y, width: w, value }) => (
            <text x={x + w + 5} y={y + 14} fill="#000" fontSize={12}>
              {Math.round(value)}
            </text>
          )}
        />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, index, payload }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 1.16;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const ishrline = Math.cos(-midAngle * RADIAN) > 0;
  const lineLength = ishrline ? 100 : -100;
  const textOffset = ishrline ? 20 : -20;
  const color = COLORS[index % COLORS.length];
  const squareSize = 12;
  return (
    <>
      <line x1={x} y1={y} x2={x + lineLength} y2={y} stroke="#ccc" strokeWidth={1} />
      <rect
        x={x + lineLength + (ishrline ? -squareSize - 0 : 0)}
        y={y - squareSize / 2}
        width={squareSize}
        height={squareSize}
        fill={color}
      />
      <text
        x={x + lineLength + textOffset}
        y={y}
        fill="#5F6D8C"
        textAnchor={ishrline ? "start" : "end"}
        dominantBaseline="central"
        fontSize={15}
      >
        <tspan>{payload.DATA} </tspan>
        <tspan fill="black" fontWeight="bold">
          {Math.round(payload.VAL)}
        </tspan>
      </text>
    </>
  );
};

const ITEMS_PER_PAGE = 10;

function NexusCarbonReport() {
  const [form] = Form.useForm();
  const userInfo = useSelector((state) => state?.authTracking?.userInfo);
  const transporterList = useSelector((state) => state?.authTracking?.transporterList);
  const reduxPermissions = useSelector((state) => state?.authTracking?.myPermissionList || {});
  const exportAllowed = useMemo(
    () => Array.isArray(reduxPermissions?.AITCFRPT) && reduxPermissions.AITCFRPT.includes("EX"),
    [reduxPermissions],
  );

  const [duration, setDuration] = useState("current_month");
  const [selectedDate, setSelectedDate] = useState(rangeFor("current_month"));
  const [formValues, setFormValues] = useState({});
  const [filterCount, setFilterCount] = useState(0);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [report, setReport] = useState({});
  const [loading, setLoading] = useState(false);
  const [chartPage, setChartPage] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tableSort, setTableSort] = useState({ field: null, order: null });
  const [CSVList, setCSVList] = useState([]);
  const [CSVLoader, setCSVLoader] = useState(false);
  const csvLinkEl = useRef();

  const [vehicles, setVehicles] = useState([]);
  const [consignors, setConsignors] = useState([]);
  const [consignees, setConsignees] = useState([]);
  useEffect(() => {
    fetchNexusAssets()
      .then((r) => setVehicles(r.response || []))
      .catch(() => setVehicles([]));
    if (userInfo?.MCID === 59 || !userInfo?.NexusCompId) return;
    fetchNexusFences("Consignor", userInfo.NexusCompId).then(setConsignors).catch(() => setConsignors([]));
    fetchNexusFences("Consignee", userInfo.NexusCompId).then(setConsignees).catch(() => setConsignees([]));
  }, [userInfo?.MCID, userInfo?.NexusCompId]);
  const driverNames = useMemo(
    () => Array.from(new Set(vehicles.map((a) => a.driver_name || a.master_driver_name).filter(Boolean))),
    [vehicles],
  );

  useEffect(() => {
    setSelectedDate(rangeFor(duration));
  }, [duration]);

  const requestBody = useCallback(
    (page, size) => ({
      dateFrom: dayjs(selectedDate?.[0]).format("YYYY-MM-DD"),
      dateTo: dayjs(selectedDate?.[1]).format("YYYY-MM-DD"),
      page,
      pageSize: size,
      vehicle: formValues?.vehicle || "",
      driver: formValues?.driver || "",
      consigner: formValues?.consignor || "",
      transporter: formValues?.transporter || "",
      consignee: formValues?.fence || "",
      tripCode: formValues?.Tripcode || "",
      tripType: formValues?.tripType || "",
      shipmentType: formValues?.shipment || "",
    }),
    [selectedDate, formValues],
  );

  const fetchReport = useCallback(async () => {
    if (!selectedDate?.[0] || !selectedDate?.[1]) return;
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "nexus/carbonreport", requestBody(tablePage, pageSize));
      setReport(data || {});
    } catch (error) {
      console.error("[NexusCarbonReport] fetch error:", error);
      message.error("Unable to load the carbon footprint report.");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, requestBody, tablePage, pageSize]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    setTablePage(1);
    setChartPage(0);
    setFilterCount(Object.values(formValues).filter((v) => v && v.length !== 0).length);
  }, [formValues, selectedDate]);

  const vehicleSeries = report?.carboonfootprintVehicle || [];
  const maxPage = Math.floor(vehicleSeries.length / ITEMS_PER_PAGE);
  const total = report?.total || 0;

  const sortRows = (rows) => {
    if (!tableSort.field) return rows;
    const numeric = ["TRIPTYPE", "ODOKMS", "AVGTRIPTIME", "CarbonFootprint"];
    return [...rows].sort((a, b) => {
      const av = a[tableSort.field];
      const bv = b[tableSort.field];
      const r = numeric.includes(tableSort.field)
        ? (Number(av) || 0) - (Number(bv) || 0)
        : String(av ?? "").localeCompare(String(bv ?? ""));
      return tableSort.order === "ascend" ? r : -r;
    });
  };

  const col = (title, dataIndex, render) => ({
    title,
    dataIndex,
    key: dataIndex,
    sorter: true,
    sortOrder: tableSort.field === dataIndex ? tableSort.order : null,
    render: render || ((v) => (v === undefined || v === null || v === "" ? "-" : v)),
  });
  const columns = [
    col("Trip Code", "TRIPCODE"),
    col("Start Date", "PickupDateTime", (value) => {
      if (!value) return "-";
      const date = new Date(value);
      return (
        <div>
          <div>{date.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })}</div>
          <div style={{ fontStyle: "italic", color: "#7d8da1" }}>
            {date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      );
    }),
    col("Vehicle", "VehRegNo"),
    col("Driver Name", "DRIVERNAME"),
    col("Trip Type", "TRIPTYPE", (type) => (type === 1 ? "Local" : type ? "Safari" : "-")),
    col("Carbon Footprint (KgCO2)", "CarbonFootprint"),
    col("ODO (Kms)", "ODOKMS", (v) => (v != null ? Number(v).toFixed(2) : "-")),
    col("Avg Trip Time", "AVGTRIPTIME", formatMinutes),
  ];

  const prepareCSV = async () => {
    try {
      setCSVLoader(true);
      const { data } = await CALL_API("POST", "nexus/carbonreport", requestBody(1, 5000));
      const rows = sortRows(data?.CarbonTable || []);
      if (!rows.length) {
        message.error("No data available to download.");
        return;
      }
      setCSVList(
        rows.map((r) => ({
          "Trip Code": r.TRIPCODE || "-",
          "Start Date": r.PickupDateTime ? dayjs(r.PickupDateTime).format("DD/MM/YYYY hh:mm A") : "-",
          Vehicle: r.VehRegNo || "-",
          "Driver Name": r.DRIVERNAME || "-",
          "Trip Type": r.TRIPTYPE === 1 ? "Local" : r.TRIPTYPE ? "Safari" : "-",
          "Carbon Footprint (KgCO2)": r.CarbonFootprint ?? "-",
          "ODO (Kms)": r.ODOKMS != null ? Number(r.ODOKMS).toFixed(2) : "-",
          "Avg Trip Time": formatMinutes(r.AVGTRIPTIME),
        })),
      );
      setTimeout(() => csvLinkEl.current?.link?.click(), 300);
    } catch (error) {
      console.error("[NexusCarbonReport] export error:", error);
      message.error("Unable to export the carbon footprint report.");
    } finally {
      setCSVLoader(false);
    }
  };

  const exportIcon = (id, title) => (
    <img
      src={upload}
      alt="Upload Icon"
      className="hover-image"
      onClick={() => {
        if (!exportAllowed) return;
        handleDownloadChart(id, title);
      }}
      style={{ cursor: exportAllowed ? "pointer" : "not-allowed" }}
    />
  );

  return (
    <>
      <Customstyle>
        <div className="head">
          <div className="head1">
            <span style={{ color: "#5F6D8C" }}>Total Carbon Footprint:</span>{" "}
            {report?.Totalcarbonfootprint?.[0]?.TotalCb ?? "--"} KgCo2
          </div>
          <div className="Custom">
            <div className="dropstyle">
              <Form.Item label="" name="dropdown">
                <Select placeholder="Custom Range" className="plc" style={{ width: "240px" }} value={duration} onChange={setDuration}>
                  {Duration.map((item) => (
                    <Option key={item.value} value={item.value}>
                      {item?.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div className="dropstyle1">
              <Space direction="vertical" size={12}>
                <RangePicker value={selectedDate} onChange={(date) => date && setSelectedDate(date)} />
              </Space>
            </div>
            <div className="badg-hover-wrapper">
              <Badge count={filterCount} className="badg">
                <Avatar
                  shape="circle"
                  size="medium"
                  src={filtericon}
                  onClick={() => setFilterModalOpen(true)}
                  className="filterstyle"
                  style={{ cursor: "pointer" }}
                />
              </Badge>
            </div>
          </div>
        </div>
        <br />

        <div id="Export Chart1">
          <Spin spinning={loading}>
            <Card style={{ width: "100%", height: "100%", overflowX: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p className="textstyle">Carbon Footprint by Vehicle</p>
                <div style={{ display: "flex", alignItems: "center", gap: "23px" }}>
                  <LeftOutlined
                    style={{ cursor: chartPage > 0 ? "pointer" : "not-allowed", color: chartPage > 0 ? "black" : "#ccc" }}
                    onClick={() => setChartPage((p) => Math.max(p - 1, 0))}
                  />
                  <RightOutlined
                    style={{ cursor: chartPage < maxPage ? "pointer" : "not-allowed", color: chartPage < maxPage ? "black" : "#ccc" }}
                    onClick={() => setChartPage((p) => Math.min(p + 1, maxPage))}
                  />
                  {exportIcon("Export Chart1", "Carbon Footprint by Vehicle")}
                </div>
              </div>
              <div style={{ width: "100%" }}>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart
                    data={vehicleSeries.slice(chartPage * ITEMS_PER_PAGE, (chartPage + 1) * ITEMS_PER_PAGE)}
                    margin={{ top: 20, right: 50, left: 10, bottom: 10 }}
                    style={{ cursor: "pointer" }}
                  >
                    <defs>
                      <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="rgba(37, 169, 114, 1)" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="rgba(217, 217, 217, 0)" stopOpacity={0.8} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="DATA" tickLine={false} interval={0} />
                    <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => Math.round(value)} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="VAL" stroke="rgba(37, 169, 114, 1)" fill="url(#colorUv)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Spin>
        </div>

        <div id="Export Chart2">
          <Spin spinning={loading}>
            <Card style={{ overflowX: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p className="textstyle">Carbon Footprints by Consignor</p>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {exportIcon("Export Chart2", "Carbon Footprints by Consignor")}
                </div>
              </div>
              <div style={{ width: "100%", height: "400px" }} className="chart-container">
                <VerticalChart
                  data={(report?.CarbonFootprintConsignee || []).map((item) => ({ Vehicle_No: item.DATA, value: item.VAL }))}
                  width="100%"
                  height={400}
                  barColor="blue"
                />
              </div>
            </Card>
          </Spin>
        </div>
        <br />

        <div id="Export Chart3">
          <Spin spinning={loading}>
            <Card style={{ overflowX: "auto" }}>
              <div style={{ position: "relative", width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <p className="textstyle">Carbon Footprint by Vehicle Type (KgCO2)</p>
                  <div style={{ display: "flex", alignItems: "center", gap: "23px" }}>
                    {exportIcon("Export Chart3", "Carbon Footprint by Vehicle Type (KgCO2)")}
                  </div>
                </div>
                <div style={{ position: "relative", width: "100%", height: "400px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        dataKey="VAL"
                        nameKey="DATA"
                        startAngle={180}
                        endAngle={0}
                        data={report?.CarbonFootprintVehicleType || []}
                        cx="50%"
                        cy="80%"
                        outerRadius={240}
                        innerRadius={120}
                        label={renderCustomizedLabel}
                        style={{ cursor: "pointer" }}
                      >
                        {(report?.CarbonFootprintVehicleType || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <img
                    src={legco2}
                    alt="Legend CO2"
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "70%",
                      transform: "translate(-50%, -50%)",
                      width: "60px",
                      height: "60px",
                      pointerEvents: "none",
                    }}
                    className="legend-image"
                  />
                </div>
              </div>
            </Card>
          </Spin>
        </div>
        <br /> <br />

        <div className="header">
          <div className="head2">
            <p>{total} Carbon Footprints</p>
          </div>
          <div className="Custom2">
            <Spin spinning={CSVLoader}>
              <img
                src={upload}
                alt="Export"
                className="hoverimage1"
                onClick={() => {
                  if (!exportAllowed) return;
                  prepareCSV();
                }}
                style={{ cursor: exportAllowed ? "pointer" : "not-allowed" }}
              />
            </Spin>
            <CSVLink data={CSVList} filename={"NexusCarbonfootprintReport.csv"} ref={csvLinkEl} />
          </div>
        </div>
        {(report?.CarbonTable || []).length > 0 && (
          <StyledTable
            dataSource={sortRows(report.CarbonTable).map((r, i) => ({ ...r, key: `${r.TRIPCODE}-${i}` }))}
            columns={columns}
            pagination={false}
            onChange={(pagination, filters, sorter) => setTableSort({ field: sorter.field, order: sorter.order })}
          />
        )}
        <ReusablePagination
          pageSize={pageSize}
          handlePageSizeChange={(v) => {
            setPageSize(v);
            setTablePage(1);
          }}
          currentPage={tablePage}
          total={total}
          handlePageChange={(p, size) => {
            setTablePage(p);
            setPageSize(size);
          }}
        />
      </Customstyle>

      <Modal
        open={filterModalOpen}
        onCancel={() => setFilterModalOpen(false)}
        width={900}
        footer={
          <ButtonFilled
            onClick={() => {
              form.resetFields();
              setFormValues({});
            }}
          >
            Clear All
          </ButtonFilled>
        }
      >
        <h6 className="Filter_for_clickablerow">Filter ({filterCount})</h6>
        <Form form={form} layout="vertical" onValuesChange={(_, all) => setFormValues(all)}>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item label="Trip Code" name="Tripcode">
                <Input className="rounded-pill" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Vehicle" name="vehicle">
                <Select
                  className="w-100"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={vehicles.map((a) => ({ label: a.asset_name, value: a.asset_id }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Driver" name="driver">
                <Select className="w-100" showSearch allowClear options={driverNames.map((n) => ({ label: n, value: n }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Consignor" name="consignor">
                <Select
                  className="w-100"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={consignors.map((f) => ({ label: f.name, value: String(f.geofence_id) }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Transporter" name="transporter">
                <Select
                  className="w-100"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={transporterList?.map((item) => ({ label: item.Name, value: item.ID }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Consignee" name="fence">
                <Select
                  className="w-100"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={consignees.map((f) => ({ label: f.name, value: f.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Trip Type" name="tripType">
                <Select className="w-100" allowClear>
                  <Option value={2}>Safari</Option>
                  <Option value={1}>Local</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Shipment Type" name="shipment">
                <Select
                  className="w-100"
                  allowClear
                  options={shipmentType?.map((item) => ({ label: item.name, value: item.value }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default withNexusReportShell(NexusCarbonReport);
